<template>
  <v-dialog
    v-model="open"
    width="600px"
    content-class="edit-playlist-modal"
    :opacity="0"
    :fullscreen="isMobileBreakpoint"
    :persistent="isMobileBreakpoint"
  >
    <v-card class="edit-playlist-modal--content" dense>
      <div :class="{'mobile-spacing': isMobileBreakpoint}">
        <v-toolbar elevation="0" class="mb-6">
          <v-toolbar-title class="pa-0">
            <span class="body-bold-medium"
              >{{
                isCreate
                  ? $t('message.playlist.modal.edit.createTitle')
                  : $t('message.playlist.modal.edit.editTitle')
              }}
            </span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="handleClose()">
              <v-icon>panda-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-form ref="form" v-model="valid" v-on:submit.prevent>
            <v-row no-gutters>
              <v-col cols="12">
                <div class="input-label-area"></div>
              </v-col>
              <v-col cols="12">
                  <base-input
                    v-model="playlistName"
                    class="playlist-text-input"
                    counter="250"
                    maxLength="250"
                    color="primary"
                    autofocus
                    outlined
                    dense
                    :label="$t('message.playlist.modal.edit.name.label')"
                    :rules="[rules.maxLength, rules.minLength]"
                    :placeholder="
                      $t('message.playlist.modal.edit.name.placeholder')
                    "
                    @keyup.enter="handleEnter"
                    @focus="handleInputState('name')"
                    @blur="handleInputState('name', 'blur')"
                  ></base-input>
              </v-col>
              <v-col v-if="isCreate" cols="12">
                <div class="input-label-area"></div>
              </v-col>
              <v-col v-if="isCreate" cols="12">
                  <base-textarea
                    v-model="playlistDescription"
                    class="playlist-text-input mb-6"
                    :counter="playlistDescription.length >= 5000 && 5000"
                    color="primary"
                    outlined
                    dense
                    :rules="[rules.maxLengthDescription]"
                    :messages="playlistDescription.length === 5000 && [$t('message.playlist.modal.rules.maxLengthDesc')]"
                    :label="$t('message.playlist.modal.edit.description.label')"
                    :placeholder="
                      $t('message.playlist.modal.edit.description.placeholder')
                    "
                    @keyup.enter="handleEnter"
                    @focus="handleInputState('description')"
                    @blur="handleInputState('description', 'blur')"
                  ></base-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </div>
      <v-divider v-if="!isMobileBreakpoint" />
      <v-card-actions
        :class="isMobileBreakpoint ? 'mobile-actions' : ''"
        class="pt-6 pb-0 px-10 px-md-0"
      >
        <v-spacer v-if="!isMobileBreakpoint" />
        <base-btn
          type="tertiary"
          class="mr-4 px-4 rounded-lg elevation-0"
          :disabled="isFetching"
          @click="handleClose"
          :width="isMobileBreakpoint ? '50%' : ''"
          keep-width-on-loading
        >
          {{ $t('message.actions.cancel') }}
        </base-btn>
        <base-btn
          type="primary"
          large
          :width="isMobileBreakpoint ? '50%' : ''"
          @click="handleConfirm"
          :tooltip="!valid && $t('message.playlist.modal.edit.reviewToCreatePlaylist')"
          :loading="isFetching"
          :disabled="!valid || isFetching || isPlaylistNameInputEmpty"
          keep-width-on-loading
        >
          {{ $t('message.actions.publish') }}
        </base-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import i18n from '@/translations/lang'
import playlistApi from '@/services/playlistApi'

export default {
  name: 'EditPlaylistModal',
  components: {
    BaseInput: () => import('@/components/BaseComponents/BaseInput.vue'),
    BaseTextarea: () => import('@/components/BaseComponents/BaseTextarea.vue'),
  },
  data: () => ({
    open: false,
    isFetching: false,
    playlist: {},
    playlistName: '',
    playlistDescription: '',
    valid: false,
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        !v.length ||
        v.length <= 250 ||
        i18n.t('message.playlist.modal.rules.maxLengthTitle'),
      minLength: (v) =>
        !v.length ||
        v.length > 2 ||
        i18n.t('message.playlist.modal.rules.minLengthTitle'),
      maxLengthDescription: (v) =>
        !v.length ||
        v.length <= 5000 ||
        i18n.t('message.playlist.modal.rules.maxLengthDesc'),
      minLengthDescription: (v) =>
        !v.length || v.length > 2 || i18n.t('message.minimumLength'),
    },
    isCreate: false,
    activeInput: null,
  }),
  watch: {
    open() {
      const refForm = this.$refs.form
      refForm && refForm.resetValidation()
    },
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    isPlaylistNameInputEmpty() {
      return !this.playlistName.length
    },
  },
  mounted() {
    this.$root.$on('editPlaylistModal', (playlist, isCreate) => {
      Object.assign(this.$data, this.initialState())
      this.playlist = playlist
      if (!isCreate) this.playlistName = playlist.name
      this.isCreate = isCreate
      this.open = true
    })
    this.$root.$on('changeLanguage', () => {
      this.rules = {
        required: i18n.t('message.requiredField'),
        maxLength: i18n.t('message.maximumLength'),
        minLength: i18n.t('message.minimumLength'),
        maxLengthDescription: i18n.t('message.maximumLength'),
        minLengthDescription: i18n.t('message.minimumLength'),
      }
    })
  },

  beforeDestroy() {
    this.$root.$off('editPlaylistModal')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('user', ['addUser', 'editUser', 'addFolder', 'editFolder']),
    handleConfirm() {
      if (this.isCreate) {
        this.handleCreate()
      } else {
        this.handleEdit()
      }
    },
    async handleCreate() {
      this.isFetching = true
      let playlistObject = {
        name: this.playlistName.trim(),
        description: this.playlistDescription.trim(),
        access_type: 'anyone_with_link',
        creator_name: this.$store.state.user?.userInfo?.name || 'Usuário',
      }
      try {
        let ret = await playlistApi.createPlaylist(playlistObject)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          type: 'success',
          text: i18n.t('message.playlist.messages.created'),
        })
        this.$root.$emit('addPlaylistVideosPage', ret.data)
        this.$router.push({
          name: 'PlaylistPage',
          params: { id: ret.data.id, create: true },
        })
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
      this.isFetching = false
      this.handleClose()
    },
    async handleEdit() {
      this.isFetching = true
      try {
        await playlistApi.updatePlaylist(
          { name: this.playlistName.trim() },
          this.playlist.id
        )
        this.playlist.name = this.playlistName
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `Playlist ${this.playlist.name} renomeada`,
          type: 'success',
        })
        this.$root.$emit('updatePlaylistPage', this.playlist)
      } catch (e) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: `${e}`,
        })
      }
      this.isFetching = false
      this.handleClose()
    },
    handleClose() {
      this.open = null
    },
    handleEnter() {
      if (this.valid) {
        this.edit ? this.handleEdit() : this.handleCreate()
      }
    },
    initialState() {
      return {
        open: false,
        isFetching: false,
        playlist: {},
        playlistName: '',
        playlistDescription: '',
        valid: false,
        rules: {
          required: (v) => (v && !!v) || i18n.t('message.requiredField'),
          maxLength: (v) =>
            !v.length ||
            v.length <= 250 ||
            i18n.t('message.playlist.modal.rules.maxLengthTitle'),
          minLength: (v) =>
            !v.length ||
            v.length > 2 ||
            i18n.t('message.playlist.modal.rules.minLengthTitle'),
          maxLengthDescription: (v) =>
            !v.length ||
            v.length <= 5000 ||
            i18n.t('message.playlist.modal.rules.maxLengthDesc'),
          minLengthDescription: (v) =>
            !v.length || v.length > 2 || i18n.t('message.minimumLength'),
        },
        isCreate: false,
      }
    },
    handleInputState(i, state) {
      if (state === 'blur') return (this.activeInput = null)
      this.activeInput = i
    },
  },
}
</script>

<style lang="scss">
.mobile-actions {
  position: absolute;
  bottom: $spacing-07;
  width: 100%;
  background-color: var(--v-background-base);
  z-index: 10;
  padding: $spacing-07 $spacing-05 !important;
  border-top: 1px solid varFigma(neutral-95-lightest);
}

.mobile-spacing {
  padding: $spacing-07 $spacing-05 !important;
}

.edit-playlist-modal {
  border-radius: 8px !important;
  border: 1px solid varFigma(neutral-95-lightest);

  &--content {
    background-color: varFigma(neutral-100-white) !important;
    padding: $spacing-07 !important;
    & header {
      height: unset !important;
      & .v-toolbar__content {
        height: unset !important;
        padding: 0 !important;
        & .v-toolbar__items button {
          justify-content: flex-end !important;
          & .theme--dark {
            color: #666d99 !important;
          }
          & .theme--light {
            color: #abadba !important;
          }
        }
      }
    }
    .input-label-area {
      height: 20px;
    }
    .playlist-text-input {
      .v-input__slot {
        margin: $spacing-02 0 !important;
        padding: 0 !important;
        .v-text-field__slot {
          padding: 0 $spacing-03 !important;
          label {
            left: 0px !important;
          }
        }
      }
      &.base-input {
        margin-bottom: $spacing-05;
      }
      .v-text-field__details {
        margin-bottom: 0 !important;
        margin-top: 2px !important;
        .v-counter {
          color: varFigma(neutral-50);
          text-align: right;
          /* regular/smaller */
          font-family: Rubik;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 150%; /* 18px */
        }
      }
    }
    .v-divider {
      margin-left: -24px !important;
      margin-right: -24px !important;
      max-width: unset !important;
    }
  }
}
</style>
